import { useState, useEffect, useCallback } from 'react'
import api from '../services/api'

/**
 * useAI — thin wrappers over the /ai endpoints with loading/error state.
 * Mirrors the useNavBadges pattern. All AI features degrade gracefully when
 * the backend has no OPENAI_API_KEY (available=false → render nothing/fallback).
 */

// One-shot fetch of a GET AI endpoint (action guide, health, risk)
export function useAIFetch(url, deps = []) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const run = useCallback(async () => {
    if (!url) return
    setLoading(true)
    setError(null)
    try {
      const r = await api.get(url)
      setData(r.data)
    } catch (e) {
      setError(e.response?.data?.detail || 'AI request failed')
    } finally {
      setLoading(false)
    }
  }, [url])

  useEffect(() => { run() }, deps)  // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error, refresh: run }
}

// Manual-trigger POST (deliverable check) — doesn't auto-run
export function useAIAction() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const run = useCallback(async (url, payload = {}) => {
    setLoading(true)
    setError(null)
    setData(null)
    try {
      const r = await api.post(url, payload)
      setData(r.data)
      return r.data
    } catch (e) {
      setError(e.response?.data?.detail || 'AI request failed')
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  return { data, loading, error, run, reset: () => setData(null) }
}
